import { forwardRef, FunctionComponent, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import Flex from '../Flex';
import Icon, { StyledIcon, registerIcon } from '../Icon';
import * as galaxyIcon from '../Icon/icons/galaxy.icon';
import Text from '../Text';
import { tryCatch } from '../../utils';
import { useI18n } from '../../hooks';
import { BaseProps, NoChildrenProp, ForwardProps } from '../../types';
import { defaultThemeProp } from '../../theme';

registerIcon(galaxyIcon);

export interface EmptyStateProps extends BaseProps, NoChildrenProp {
  /**
   * Message to display below the icon.
   * @default 'No items'
   */
  message?: string;
  ref?: Ref<HTMLDivElement>;
}

export const StyledEmptyState = styled.div(({ theme }) => {
  const color = tryCatch(() =>
    rgba(theme.base.palette['foreground-color'], theme.base.transparency['transparent-2'])
  );
  return css`
    height: 100%;

    ${StyledIcon} {
      font-size: 1.5rem;
      height: 1.5rem;
      width: 1.5rem;
      color: ${color};
    }
  `;
});

StyledEmptyState.defaultProps = defaultThemeProp;

const EmptyState: FunctionComponent<EmptyStateProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<EmptyStateProps>, ref: EmptyStateProps['ref']) => {
    const t = useI18n();
    const { message = t('no_items'), ...restProps } = props;

    return (
      <Flex
        {...restProps}
        as={StyledEmptyState}
        ref={ref}
        container={{ direction: 'column', alignItems: 'center', justify: 'center', gap: 1 }}
      >
        <Icon name='galaxy' />
        <Text variant='secondary'>{message}</Text>
      </Flex>
    );
  }
);

export default EmptyState;
